import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, DatePicker, Input, Modal, Segmented, Spin, Table, Tag, Tooltip, message } from 'antd';
import {
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  ThunderboltOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ReactSVG } from 'react-svg';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import {
  getRouteInconsistencies,
  dismissRouteInconsistency,
  correctRouteShiftEnd,
  correctInconsistentShifts,
  revertRouteShiftCorrection,
} from '../modules/routes/api';

const { RangePicker } = DatePicker;

// Human-readable label + explanation per inconsistency type. New detection
// cases (future) just add an entry here.
const TYPE_META = {
  // Hard, unambiguous: recorded end is before the actual last delivery.
  route_end_before_last_delivery: {
    color: 'red',
    label: 'inconsistencies.types.endBeforeDelivery',
    hint: 'inconsistencies.types.endBeforeDeliveryHint',
  },
  // Soft drift: recorded end is far from (actual start + planned duration).
  shift_end_drift: {
    color: 'orange',
    label: 'inconsistencies.types.drift',
    hint: 'inconsistencies.types.driftHint',
  },
  // Driver started the route AT the first pickup — didn't badge the bike
  // pick-up at the hub, so the start time (and client-service) are wrong.
  start_missing_bike_pickup: {
    color: 'orange',
    label: 'inconsistencies.types.startMissingPickup',
    hint: 'inconsistencies.types.startMissingPickupHint',
  },
  // OVO-260 — synthetic type for a corrected route that no longer trips any
  // check (the correction resolved it). Only ever shown on the Corrected tab.
  corrected: {
    color: 'green',
    label: 'inconsistencies.types.corrected',
    hint: 'inconsistencies.types.correctedHint',
  },
};

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM HH:mm') : '—');

// Why the backend refused to auto-correct a route — shown verbatim in the bulk
// report so a skip is never mistaken for a silent failure.
const CANNOT_LABEL = {
  start_badge_issue_needs_manual_review: 'inconsistencies.cannot.startIssue',
  no_validated_stop_to_anchor_on: 'inconsistencies.cannot.noValidatedStop',
  correction_would_not_improve: 'inconsistencies.cannot.noImprovement',
  already_corrected: 'inconsistencies.cannot.alreadyCorrected',
  no_inconsistency_detected: 'inconsistencies.cannot.noInconsistency',
};

const SKIP_REASON = {
  start_badge_issue_needs_manual_review: 'inconsistencies.skip.startIssue',
  no_validated_stop_to_anchor_on: 'inconsistencies.skip.noValidatedStop',
  correction_would_not_improve: 'inconsistencies.skip.noImprovement',
  already_corrected: 'inconsistencies.skip.alreadyCorrected',
  no_inconsistency_detected: 'inconsistencies.skip.noInconsistency',
};

/**
 * Monitoring → Inconsistencies. Read-only list of routes whose recorded end
 * time looks unreliable. Two checks: a hard one (end before the actual last
 * delivery — impossible) and a soft drift one (recorded end far from the
 * driver's actual start + the planned route duration). Nothing is auto-fixed —
 * the operator opens each route and corrects it via "Fix shift times".
 */
const InconsistenciesPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [range, setRange] = useState([dayjs().subtract(7, 'day'), dayjs().add(7, 'day')]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [shiftOverlaps, setShiftOverlaps] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [dismissTarget, setDismissTarget] = useState(null); // the row being dismissed
  const [dismissReason, setDismissReason] = useState('');
  const [dismissSaving, setDismissSaving] = useState(false);
  // OVO-260 — 'open' = still to handle (what the menu badge counts);
  // 'corrected' = auto-corrected, kept for review (Felipe: "so Seb can review").
  const [tab, setTab] = useState('open');
  const [correctingId, setCorrectingId] = useState(null);
  // Filter drawer — client-side, the scan result is fully loaded.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [bulkPreview, setBulkPreview] = useState(null); // dry-run report awaiting confirmation
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(
    async (r, nextTab) => {
      const [from, to] = r || range;
      const which = nextTab || tab;
      setLoading(true);
      try {
        const { response, error } = await getRouteInconsistencies({
          dateFrom: from.format('YYYY-MM-DD'),
          dateTo: to.format('YYYY-MM-DD'),
          onlyCorrected: which === 'corrected' ? 'true' : undefined,
        });
        // e.g. a range wider than the backend's 92-day limit (#160).
        if (error) messageApi.error(error.message || t('common:error'));
        setRows(response?.inconsistencies || []);
        setShiftOverlaps(response?.shiftOverlaps || []);
        setScanned(response?.scanned || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, tab],
  );

  useEffect(() => {
    load(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- OVO-260 — auto-correction handlers -------------------------------
  const correctOne = async (rec) => {
    setCorrectingId(rec.routeId);
    try {
      const { response } = await correctRouteShiftEnd(rec.routeId);
      messageApi.success(
        t('inconsistencies.correctedOne', {
          id: rec.routeId,
          time: fmtTime(response?.to),
        }),
      );
      load(range);
    } catch (e) {
      // The backend refuses rather than guessing (e.g. no validated stop to
      // anchor on) — surface that reason instead of a generic failure.
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setCorrectingId(null);
    }
  };

  const previewBulk = async () => {
    setBulkRunning(true);
    try {
      const { response } = await correctInconsistentShifts({
        dateFrom: range[0].format('YYYY-MM-DD'),
        dateTo: range[1].format('YYYY-MM-DD'),
        dryRun: true,
      });
      setBulkPreview(response);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setBulkRunning(false);
    }
  };

  const applyBulk = async () => {
    setBulkRunning(true);
    try {
      const { response } = await correctInconsistentShifts({
        dateFrom: range[0].format('YYYY-MM-DD'),
        dateTo: range[1].format('YYYY-MM-DD'),
        dryRun: false,
      });
      messageApi.success(t('inconsistencies.bulkDone', { count: response?.corrected ?? 0 }));
      setBulkPreview(null);
      load(range);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setBulkRunning(false);
    }
  };

  const undoCorrection = async (rec) => {
    setCorrectingId(rec.routeId);
    try {
      await revertRouteShiftCorrection(rec.routeId);
      messageApi.success(t('inconsistencies.correctionUndone', { id: rec.routeId }));
      load(range);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setCorrectingId(null);
    }
  };

  const submitDismiss = async () => {
    if (!dismissTarget) return;
    setDismissSaving(true);
    try {
      const { response } = await dismissRouteInconsistency(dismissTarget.routeId, dismissReason);
      if (response) {
        messageApi.success(t('inconsistencies.dismissed'));
        setDismissTarget(null);
        setDismissReason('');
        load(range);
      } else {
        messageApi.error(t('common:error'));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setDismissSaving(false);
    }
  };

  const columns = [
    {
      title: t('inconsistencies.route'),
      dataIndex: 'routeId',
      key: 'routeId',
      render: (id) => <Link to={`/admin/routes/${id}`}>#{id}</Link>,
    },
    {
      title: t('inconsistencies.date'),
      dataIndex: 'date',
      key: 'date',
      render: (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
    },
    {
      title: t('inconsistencies.driver'),
      dataIndex: 'driverName',
      key: 'driverName',
      render: (v) => v || '—',
    },
    {
      title: t('inconsistencies.issue'),
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const meta = TYPE_META[type] || { color: 'orange', label: type, hint: '' };
        return (
          <Tooltip title={meta.hint ? t(meta.hint) : undefined}>
            <Tag color={meta.color}>{meta.label.includes('.') ? t(meta.label) : meta.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('inconsistencies.recordedEnd'),
      dataIndex: 'recordedEnd',
      key: 'recordedEnd',
      render: fmtTime,
    },
    {
      title: <Tooltip title={t('inconsistencies.expectedEndHint')}>{t('inconsistencies.expectedEnd')}</Tooltip>,
      dataIndex: 'referenceTime',
      key: 'referenceTime',
      render: (iso, rec) => (
        <Tooltip
          title={`${t('inconsistencies.actualStart')}: ${fmtTime(rec.actualShiftStart)} · ${t(
            'inconsistencies.plannedHub',
          )}: ${fmtTime(rec.plannedHubArrival)}`}
        >
          {fmtTime(iso)}
        </Tooltip>
      ),
    },
    {
      title: t('inconsistencies.offBy'),
      dataIndex: 'offByMins',
      key: 'offByMins',
      sorter: (a, b) => Math.abs(a.offByMins) - Math.abs(b.offByMins),
      defaultSortOrder: 'descend',
      render: (m, rec) => {
        // Hard case: minutes BEFORE the last delivery (always "early").
        // Drift case: signed — positive = ended late, negative = ended early.
        const sign = m > 0 ? '+' : '';
        const label =
          rec.type === 'route_end_before_last_delivery'
            ? t('inconsistencies.beforeDeliveryBy', { mins: m })
            : `${sign}${m} min`;
        return <Tag color="red">{label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 320,
      render: (_, rec) =>
        tab === 'corrected' ? (
          <Tooltip title={t('inconsistencies.undoCorrectionHint')}>
            <Button
              size="small"
              icon={<UndoOutlined />}
              loading={correctingId === rec.routeId}
              onClick={() => undoCorrection(rec)}
            >
              {t('inconsistencies.undoCorrection')}
            </Button>
          </Tooltip>
        ) : (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* The backend decides per row whether a correction is possible and
                says why not, so the button is only ever offered when it will
                actually do something. Otherwise: a flag with the reason. */}
            {rec.correctable ? (
              <Tooltip title={t('inconsistencies.correctHint')}>
                <Button
                  size="small"
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={correctingId === rec.routeId}
                  onClick={() => correctOne(rec)}
                >
                  {t('inconsistencies.correct')}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  SKIP_REASON[rec.correctionReason]
                    ? t(SKIP_REASON[rec.correctionReason])
                    : t('inconsistencies.cannotAutoCorrectHint')
                }
              >
                <Tag style={{ margin: 0 }}>
                  {t(CANNOT_LABEL[rec.correctionReason] || 'inconsistencies.cannotAutoCorrect')}
                </Tag>
              </Tooltip>
            )}
            <Tooltip title={t('inconsistencies.markFalsePositiveHint')}>
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setDismissReason('');
                  setDismissTarget(rec);
                }}
              >
                {t('inconsistencies.markFalsePositive')}
              </Button>
            </Tooltip>
            {/* OVO-117 — hand the flagged route to the support queue. */}
            <Tooltip title={t('tickets.createFromHere')}>
              <Button
                size="small"
                icon={<CustomerServiceOutlined />}
                onClick={() =>
                  navigate('/admin/tickets', {
                    state: {
                      create: {
                        routeId: rec.routeId,
                        category: 'PLANNING_ROUTE_ISSUE',
                        subject: `Route #${rec.routeId} — ${rec.type || 'inconsistency'}`,
                        internal: true,
                      },
                    },
                  })
                }
              />
            </Tooltip>
          </div>
        ),
    },
  ];

  // On the Corrected tab, show what the badge USED to say next to the new value.
  const correctedColumns = columns.map((c) =>
    c.key === 'recordedEnd'
      ? {
          ...c,
          title: t('inconsistencies.correctedEnd'),
          render: (iso, rec) => (
            <div>
              <div>{fmtTime(iso)}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'line-through' }}>
                {fmtTime(rec.correctedFromEnd)}
              </div>
            </div>
          ),
        }
      : c,
  );

  const driverOptions = useMemo(() => {
    const names = [...new Set(rows.map((r) => r.driverName).filter(Boolean))].sort();
    return names.map((n) => ({ value: n, label: n }));
  }, [rows]);

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (filters.driverName && r.driverName !== filters.driverName) return false;
        if (filters.type && r.type !== filters.type) return false;
        return true;
      }),
    [rows, filters],
  );

  // Same Settings drawer as every other list (page size + columns). The
  // Corrected tab swaps one column's renderer, so the config drives the base
  // set and that swap is applied on top of whatever is visible.
  const FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('inconsistencies-columns', columns, FIXED_KEYS);
  const displayColumns =
    tab === 'corrected'
      ? columnConfig.visibleColumns.map((c) => correctedColumns.find((cc) => cc.key === c.key) || c)
      : columnConfig.visibleColumns;

  return (
    <div className="InconsistenciesPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Modal
        title={t('inconsistencies.markFalsePositive')}
        open={!!dismissTarget}
        onCancel={() => setDismissTarget(null)}
        onOk={submitDismiss}
        okText={t('inconsistencies.markFalsePositive')}
        confirmLoading={dismissSaving}
        cancelText={t('common:cancel')}
      >
        <p style={{ marginTop: 0, color: '#555' }}>
          {t('inconsistencies.markFalsePositiveDesc', { id: dismissTarget?.routeId })}
        </p>
        <Input.TextArea
          rows={3}
          autoFocus
          value={dismissReason}
          onChange={(e) => setDismissReason(e.target.value)}
          placeholder={t('inconsistencies.reasonPlaceholder')}
        />
      </Modal>
      {/* OVO-260 — dry-run preview. Correcting shift times rewrites driver
          badge data, so the operator sees exactly what will change (and what
          will be skipped, and why) before confirming. */}
      <Modal
        title={t('inconsistencies.correctAll')}
        open={!!bulkPreview}
        onCancel={() => setBulkPreview(null)}
        onOk={applyBulk}
        okText={t('inconsistencies.correctAllConfirm', { count: bulkPreview?.corrected ?? 0 })}
        okButtonProps={{ disabled: !bulkPreview?.corrected }}
        confirmLoading={bulkRunning}
        cancelText={t('common:cancel')}
        width={620}
      >
        {bulkPreview && (
          <>
            <Alert
              type={bulkPreview.corrected ? 'info' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
              message={t('inconsistencies.bulkPreviewTitle', {
                corrected: bulkPreview.corrected,
                candidates: bulkPreview.candidates,
              })}
              description={t('inconsistencies.bulkPreviewDesc', {
                hours: Math.round((bulkPreview.totalMinutesRemoved || 0) / 60),
              })}
            />
            {Object.keys(bulkPreview.skippedByReason || {}).length > 0 && (
              <div style={{ color: '#555' }}>
                <strong>{t('inconsistencies.bulkSkipped')}</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {Object.entries(bulkPreview.skippedByReason).map(([reason, n]) => (
                    <li key={reason}>
                      {n} — {SKIP_REASON[reason] ? t(SKIP_REASON[reason]) : reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Modal>
      {shiftOverlaps.length > 0 && (
        <Card
          size="small"
          style={{ marginBottom: 12, borderColor: '#faad14' }}
          title={
            <span style={{ color: '#d46b08' }}>
              ⚠ Overlapping shifts ({shiftOverlaps.length}) — one driver counts as several vehicles in the optimization
            </span>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {shiftOverlaps.map((g) => (
              <div
                key={`${g.driverId}-${g.date}`}
                style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}
              >
                <Tag color="orange">{g.date}</Tag>
                <b>{g.driverName || `Driver #${g.driverId}`}</b>
                {g.shifts.map((sf) => (
                  <Tag key={sf.id}>
                    #{sf.id} {sf.start}–{sf.end}
                    {sf.fleetName ? ` · ${sf.fleetName}` : ''}
                    {sf.routeCount ? ` · ${sf.routeCount} route(s)` : ''}
                  </Tag>
                ))}
                <Link to="/admin/scheduler">open Scheduler</Link>
              </div>
            ))}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            Delete or adjust the extra shift(s) in the Scheduler before the day is optimized — shifts with routes
            attached need their routes removed first. New overlaps are now rejected at creation.
          </div>
        </Card>
      )}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
              {t('inconsistencies.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 720, marginTop: 4 }}>
              {t('inconsistencies.subtitle')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <RangePicker
              value={range}
              allowClear={false}
              onChange={(r) => {
                if (r && r[0] && r[1]) {
                  setRange(r);
                  load(r);
                }
              }}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => load(range)}>
              {t('inconsistencies.checkNow')}
            </Button>
            {/* OVO-260 — bulk auto-correction. Always previews (dry run) first;
                the operator confirms the exact count before anything is written. */}
            {tab === 'open' && (
              <Tooltip title={t('inconsistencies.correctAllHint')}>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={bulkRunning && !bulkPreview}
                  onClick={previewBulk}
                >
                  {t('inconsistencies.correctAll')}
                </Button>
              </Tooltip>
            )}
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <Segmented
          style={{ marginBottom: 16 }}
          value={tab}
          onChange={(v) => {
            setTab(v);
            load(range, v);
          }}
          options={[
            { label: t('inconsistencies.tabOpen'), value: 'open' },
            { label: t('inconsistencies.tabCorrected'), value: 'corrected' },
          ]}
        />

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <Alert
              type="success"
              showIcon
              message={t('inconsistencies.allClear')}
              description={t('inconsistencies.scannedNote', { count: scanned })}
            />
          ) : (
            <>
              <div style={{ marginBottom: 8, color: 'var(--text-muted)' }}>
                {t('inconsistencies.foundNote', { found: rows.length, scanned })}
              </div>
              <Table
                rowKey="routeId"
                dataSource={filteredRows}
                columns={displayColumns}
                pagination={{ pageSize: columnConfig.pageSize, showSizeChanger: false, hideOnSinglePage: true }}
                size="middle"
                scroll={{ x: 'max-content' }}
              />
            </>
          )}
        </Spin>
        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={setFilters}
          fields={[
            { name: 'driverName', label: t('inconsistencies.driver'), options: driverOptions, placeholder: '—' },
            {
              name: 'type',
              label: t('inconsistencies.issue'),
              placeholder: '—',
              options: Object.entries(TYPE_META)
                .filter(([k]) => k !== 'corrected')
                .map(([value, meta]) => ({ value, label: t(meta.label) })),
            },
          ]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={columnConfig.setPageSize}
        />
      </Card>
    </div>
  );
};

export default InconsistenciesPage;
